import { HydratorResolver } from "@its-engineering-and-development/rbac-react";
import { store } from "@/store";
import { setPermissions, type PermissionRole } from "@/store/permissions.slice";
import { MODULE_KEYS } from "./permission-actions";

// The permissions service emits all module actions under the `expense.*`
// domain (Users/Groups/Permissions included). Keys outside this prefix are
// filtered out server-side.
const DEFAULT_PERMISSION_DOMAINS = "expense";

/**
 * Maps the coarse actions emitted by the permissions service (which aggregates
 * roles into `expense.admin:*` / `expense.approvals:*`) to the granular
 * per-module actions the app routes and sidebar are gated on.
 */
const COARSE_ACTION_TO_MODULES: Record<string, readonly string[]> = {
    "expense.admin": [
        MODULE_KEYS.users,
        MODULE_KEYS.groups,
        MODULE_KEYS.permissions,
        MODULE_KEYS.manageCategories,
        MODULE_KEYS.manageCustomData,
        MODULE_KEYS.policyExceptions,
        MODULE_KEYS.policyReceipt,
        MODULE_KEYS.utilities,
    ],
    "expense.approvals": [MODULE_KEYS.manageApprovals],
};

interface PermissionsResponse {
    roles?: unknown;
    allowedActions?: unknown;
}

// ============================================================================
// localStorage cache
// ============================================================================

const PERMISSIONS_STORAGE_KEY = "itseng_permissions_cache";
const MAX_CACHE_AGE_MS = 14 * 60 * 1000; // 14 minutes
const PERMISSIONS_REQUEST_TIMEOUT_MS = 10_000;

interface StoredPermissions {
    sub: string;
    fingerprint: string;
    allowedActions: string[];
    roles: PermissionRole[];
    cachedAt: number;
}

function getTokenSub(token: string): string {
    try {
        const raw = token.split(".")[1];
        if (!raw) return "";
        // JWT uses base64url; normalize to standard base64 before decoding.
        const base64 = raw
            .replace(/-/g, "+")
            .replace(/_/g, "/")
            .padEnd(Math.ceil(raw.length / 4) * 4, "=");
        const payload = JSON.parse(atob(base64)) as Record<string, unknown>;
        return typeof payload.sub === "string" ? payload.sub : "";
    } catch {
        return "";
    }
}

function readStoredPermissions(token: string): StoredPermissions | null {
    try {
        const raw = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw) as Record<string, unknown>;
        if (
            typeof p.sub !== "string" ||
            typeof p.fingerprint !== "string" ||
            typeof p.cachedAt !== "number" ||
            !Array.isArray(p.allowedActions) ||
            !Array.isArray(p.roles) ||
            !(p.allowedActions as unknown[]).every((a) => typeof a === "string") ||
            !(p.roles as unknown[]).every(
                (r) => typeof (r as Record<string, unknown>)?.roleId === "string" && typeof (r as Record<string, unknown>)?.roleName === "string",
            )
        ) {
            return null;
        }
        if (Date.now() - p.cachedAt > MAX_CACHE_AGE_MS) return null;
        // Reject a cache entry belonging to a different user.
        if (p.sub !== getTokenSub(token)) return null;
        return {
            sub: p.sub as string,
            fingerprint: p.fingerprint as string,
            allowedActions: p.allowedActions as string[],
            roles: p.roles as PermissionRole[],
            cachedAt: p.cachedAt as number,
        };
    } catch {
        return null;
    }
}

function writeStoredPermissions(data: StoredPermissions): void {
    try {
        localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(data));
    } catch {
        // quota or security policy error — non-fatal
    }
}

function clearStoredPermissions(): void {
    try {
        localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
    } catch {
        // ignore
    }
}

function grant(actions: Set<string>, moduleKey: string, verb: "read" | "write"): void {
    actions.add(`${moduleKey}:${verb}`);
    // A write grant always implies read on the same module.
    if (verb === "write") {
        actions.add(`${moduleKey}:read`);
    }
}

function expandAction(actions: Set<string>, raw: string): void {
    const value = raw.trim();
    const separator = value.lastIndexOf(":");
    if (separator === -1) {
        return;
    }

    // Preserve the moduleKey casing (e.g. `expense.manage.custom.data`) since
    // permission checks are exact-match; only the verb is normalized.
    const moduleKey = value.slice(0, separator);
    const verb = value.slice(separator + 1).toLowerCase();
    if (verb !== "read" && verb !== "write") {
        return;
    }

    const mappedModules = COARSE_ACTION_TO_MODULES[moduleKey.toLowerCase()];
    if (mappedModules) {
        for (const mapped of mappedModules) {
            grant(actions, mapped, verb);
        }
        return;
    }

    // Pass-through: the backend already returned a granular action the app understands.
    grant(actions, moduleKey, verb);
}

// Module-level deduplication cache — keyed on the sorted action fingerprint.
let _lastFingerprint: string | null = null;
let _lastResult: string[] | null = null;
let _lastValidatedAt: number | null = null;
let _cacheGeneration = 0;

/** Stable fingerprint for sorted action lists; order-independent string comparison. */
function fingerprintActions(actions: string[]): string {
    return JSON.stringify([...actions].sort());
}

async function fetchAndApplyPermissions(token: string, requestGeneration: number): Promise<string[]> {
    const baseUrl = (import.meta.env.VITE_AUTH_SERVICE_URL ?? "").trim().replace(/\/$/, "");
    if (!baseUrl) {
        throw new Error("VITE_AUTH_SERVICE_URL is not configured");
    }

    const domains = (import.meta.env.VITE_PERMISSION_DOMAINS ?? DEFAULT_PERMISSION_DOMAINS).trim() || DEFAULT_PERMISSION_DOMAINS;
    const url = `${baseUrl}/permissions?domains=${encodeURIComponent(domains)}`;

    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            controller.abort();
            reject(new Error("Loading permissions timed out after 10 seconds"));
        }, PERMISSIONS_REQUEST_TIMEOUT_MS);
    });

    let response: Response;
    try {
        response = await Promise.race([
            fetch(url, {
                cache: "no-store",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
                signal: controller.signal,
            }),
            timeout,
        ]);
    } finally {
        clearTimeout(timeoutId);
    }

    if (!response.ok) {
        throw new Error(`Failed to load permissions (${response.status})`);
    }

    const data = (await response.json()) as PermissionsResponse;
    if (requestGeneration !== _cacheGeneration) {
        return _lastResult ?? [];
    }

    const rawActions = Array.isArray(data.allowedActions) ? data.allowedActions : [];

    const actions = new Set<string>();

    // Dashboard is no longer auto-granted: access to any module (including the
    // dashboard) must be explicitly returned by the permissions service. A user
    // with zero backend actions resolves to an empty set so the app can detect
    // "no access" and log them out.
    for (const raw of rawActions) {
        if (typeof raw === "string") {
            expandAction(actions, raw);
        }
    }

    const resolved = [...actions];
    const fingerprint = fingerprintActions(resolved);
    const roles: PermissionRole[] = Array.isArray(data.roles)
        ? (data.roles as PermissionRole[]).filter((r) => typeof r?.roleId === "string" && typeof r?.roleName === "string")
        : [];
    const validatedAt = Date.now();

    _lastValidatedAt = validatedAt;

    if (fingerprint === _lastFingerprint && _lastResult !== null) {
        writeStoredPermissions({ sub: getTokenSub(token), fingerprint, allowedActions: _lastResult, roles, cachedAt: validatedAt });
        return _lastResult;
    }

    _lastFingerprint = fingerprint;
    _lastResult = resolved;
    store.dispatch(setPermissions({ allowedActions: resolved, roles }));
    writeStoredPermissions({ sub: getTokenSub(token), fingerprint, allowedActions: resolved, roles, cachedAt: validatedAt });

    return resolved;
}

async function hydrateAllowedActions(token: string): Promise<string[]> {
    const requestGeneration = _cacheGeneration;

    // Stale-while-revalidate on page reload: seed synchronously from a valid
    // localStorage entry so the shell renders without a flash, then revalidate
    // against the network in the background so admin-side permission changes
    // (including full revocation → auto-logout) propagate on the same load
    // instead of waiting for the next background refresh.
    if (_lastResult === null) {
        const stored = readStoredPermissions(token);
        if (stored !== null) {
            _lastFingerprint = stored.fingerprint;
            _lastResult = stored.allowedActions;
            _lastValidatedAt = stored.cachedAt;
            store.dispatch(setPermissions({ allowedActions: stored.allowedActions, roles: stored.roles }));
            void fetchAndApplyPermissions(token, requestGeneration).catch(() => {
                // Non-fatal: the seeded cache stays in effect until the next successful load.
                console.warn("Background permissions revalidation failed");
            });
            return stored.allowedActions;
        }
    }

    return fetchAndApplyPermissions(token, requestGeneration);
}

/** Reset the in-memory deduplication cache and the localStorage entry. Exposed for testing and logout cleanup. */
export function resetPermissionsCache(): void {
    _cacheGeneration += 1;
    _lastFingerprint = null;
    _lastResult = null;
    _lastValidatedAt = null;
    clearStoredPermissions();
}

export function getPermissionsLastValidatedAt(): number | null {
    return _lastValidatedAt;
}

// Invalidate pending responses whenever the active session token changes.
let _previousAccessToken = store.getState().auth.accessToken;
store.subscribe(() => {
    const accessToken = store.getState().auth.accessToken;
    if (accessToken !== _previousAccessToken) {
        resetPermissionsCache();
    }
    _previousAccessToken = accessToken;
});

export const expenseAdminPermissionResolver = new HydratorResolver(hydrateAllowedActions);
